<!-- src/views/Home.vue (or replace your current page file) -->
<script>
import { useCarStore } from '@/stores/carStore.js'

export default {
  name: 'HomePage',
  data() {
    return {
      store: useCarStore(),
      loading: true, // local loading to keep your splash animation timing
      backgroundImages: [
        'https://i.postimg.cc/6qF4FVqb/Iq0Lplw.jpg',
        'https://i.postimg.cc/Fzwkwbzn/Ukt0Fpz.jpg',
        'https://i.postimg.cc/hG6V18n4/fvVwnER.jpg'
      ],
      currentBackgroundIndex: 0,
      currentBackground: 'https://i.postimg.cc/hG6V18n4/fvVwnER.jpg'
    }
  },
  async created() {
    // fetch from Pinia store (handles private/public repo in one place)
    if (!Array.isArray(this.store.cars) || this.store.cars.length === 0) {
      try {
        await this.store.fetchCars()
      } catch (e) {
        console.error('[Home] fetchCars failed:', e)
      }
    }
  },
  mounted() {
    this.startBackgroundRotation()
    document.title = 'AFM | Affordable Motors Billingham'

    // keep your logo overlay feel; settle local loading after a short delay
    const done = () => { this.loading = false }
    if (this.store.loading) {
      const unwatch = this.$watch(
        () => this.store.loading,
        (val) => { if (!val) { setTimeout(done, 800); unwatch() } },
        { immediate: false }
      )
    } else {
      setTimeout(done, 800)
    }
  },
  computed: {
    cars() {
      return Array.isArray(this.store.cars) ? this.store.cars : []
    },
    hasCars() {
      return this.cars.length > 0
    },
    // Top 3 by explicit IDs 1,2,3 like your original logic
    featuredCars() {
      const desired = [1, 2, 3]
      return desired
        .map(id => this.cars.find(c => Number(c.ID) === id))
        .filter(Boolean)
    }
  },
  methods: {
    formatPrice(price) {
      const n = Number(price) || 0
      return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n)
    },
    formatMileage(mileage) {
      const n = Number(mileage) || 0
      return n.toLocaleString('en-GB')
    },
    safeFirstImage(csv) {
      if (!csv) return ''
      const first = String(csv).split(',').map(s => (s || '').trim()).filter(Boolean)[0]
      return first || ''
    },
    startBackgroundRotation() {
      setInterval(() => {
        this.currentBackgroundIndex = (this.currentBackgroundIndex + 1) % this.backgroundImages.length
        this.currentBackground = ''
        this.$nextTick(() => {
          this.currentBackground = this.backgroundImages[this.currentBackgroundIndex]
        })
      }, 5000)
    }
  },
  directives: {
    intersect: {
      mounted(el) {
        const observer = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) {
              el.classList.add('appear')
              observer.unobserve(el)
            }
          },
          { threshold: 0.2 }
        )
        observer.observe(el)
      }
    }
  }
}
</script>

<template>
  <!-- Loading Animation -->
  <div v-if="loading" class="loading-overlay fade-out-on-hide">
    <img src="../assets/AW TRADE CENTRE LOGO.png" alt="Logo" class="loading-logo" />
  </div>

  <!-- Hero Section -->
  <section class="hero-section" :style="{ backgroundImage: `url(${currentBackground})` }">
    <div class="hero-overlay">
      <div class="hero-content">
        <h1>Back on the Road</h1>
        <p>Find the best cars with top-quality service</p>
        <router-link to="/cars" class="btn-dark btn btn-lg">Browse Cars</router-link>
      </div>
    </div>
  </section>

  <!-- Info Section -->
  <section class="info-section py-6">
    <div class="container d-flex flex-column flex-lg-row align-items-center gap-5">
      <img src="../assets/dealer.jpg" class="info-image slide-in-left" alt="Showroom" v-intersect />
      <div class="text-light fade-slide-up" v-intersect>
        <h2 class="info-title mb-3">Trusted Specialist in High-Quality, Tested Used Cars - Based in Billingham, Teesside</h2>
        <p class="info-description">
          At AFM Billingham, we deliver affordable, reliable vehicles to drivers across the UK.
          With our carefully selected cars in stock, we offer outstanding value across a wide range of makes and models — whether you're looking for something budget-friendly, practical, or a bit of both.
          <br /><br />
          We make car buying simple and stress-free. Explore our full inventory online, and buy your next car from the comfort of your home.
          Prefer to see it in person? <strong><a href="/contact">get in touch with us</a></strong>
          and let our experienced team help you find the perfect fit.
          <br /><br />
          At AFM Billingham, we believe quality, affordable cars should be within everyone's reach. With a focus on transparency, value, and customer satisfaction, we’re here to help you drive away with confidence.
        </p>
      </div>
    </div>
  </section>

  <!-- Featured Cars -->
  <section class="recent-cars py-5 bg-black">
    <div class="container">
      <h2 class="text-center mb-4 text-light">Featured cars</h2>

      <div v-if="hasCars" class="row">
        <div class="col-md-4 mb-4" v-for="item in featuredCars" :key="item.ID">
          <router-link
            :to="{ name: 'carVue', params: { id: item.ID } }"
            class="card h-100 text-decoration-none custom-card fade-slide-up"
            v-intersect
          >
            <img :src="safeFirstImage(item.Image)" class="card-img-top" alt="Car Image" />
            <div class="card-body">
              <h5 class="card-text">{{ item.YearOfManu }} {{ item.Name }} {{ item.Model }}</h5>
              <h6 class="card-text">{{ item.EyeCatcher }}</h6>
              <h6 class="card-text">
                <span class="highlights">{{ formatMileage(item.Mileage) }} Miles</span>
              </h6>
              <h5 class="card-title">{{ formatPrice(item.Value) }}</h5>
            </div>
          </router-link>
        </div>
      </div>

      <div v-else class="text-center text-light-50">
        <p class="mb-3">No featured vehicles to show yet.</p>
      </div>

      <div class="text-center mt-4">
        <router-link to="/cars" class="btn btn-dark btn-lg">View More Cars</router-link>
      </div>
    </div>
  </section>
</template>

<style scoped>
a { text-decoration: none; color: white; }

/* Hero Section */
.hero-section {
  position: relative;
  background-attachment: scroll;
  background-size: cover;
  background-position: center;
  min-height: 100svh;
  height: 100vh;
  will-change: auto;
}
.hero-overlay {
  position: absolute; top: 0; left: 0; width: 100%; height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
}
.hero-content {
  position: relative; z-index: 2; color: #fff; text-align: center;
  top: 50%; transform: translateY(-50%);
}
.hero-content h1 { font-size: 3rem; margin-bottom: 1rem; }
.hero-content p { font-size: 1.5rem; margin-bottom: 2rem; }

/* Loading Overlay */
.loading-overlay {
  position: fixed; top: 0; left: 0; width: 100%; height: 100%;
  background-color: #000000b4; z-index: 9999;
  display: flex; align-items: center; justify-content: center;
  animation: logoFadeZoom 3.5s ease-in-out forwards; animation-delay: 2s;
}
.loading-logo { width: 25em; height: auto; opacity: 0; transform: scale(1); animation: logoFadeZoom 2.8s ease forwards; }

@keyframes logoFadeZoom {
  0% { opacity: 0; transform: scale(0.85); }
  40% { opacity: 1; transform: scale(1); }
  70% { opacity: 1; transform: scale(1.05); }
  100% { opacity: 0; transform: scale(1.1); }
}

/* Info Section */
.info-section { padding-top: 10px; background-color: #111; color: #fff; }
.info-image {
  max-width: 50%; height: auto; border-radius: 12px;
  box-shadow: 0 8px 20px rgba(255, 255, 255, 0.05);
}
.info-title { font-size: 1.8rem; font-weight: 600; }
.info-description { font-size: 1rem; line-height: 1.8; color: #ccc; }

/* Car Cards */
.custom-card { background-color: #121212; border: 1px solid #2c2c2c; color: white; transition: all 0.3s ease-in-out; }
.custom-card:hover { transform: translateY(-5px); box-shadow: 0 8px 15px rgba(255, 255, 255, 0.1); }
.card-img-to p { object-fit: cover; width: 100%; border-bottom: 1px solid #2c2c2c; }

/* Appear Animations */
.fade-slide-up { opacity: 0; transform: translateY(40px); transition: opacity 1.2s ease-out, transform 1.2s ease-out; }
.fade-slide-up.appear { opacity: 1; transform: translateY(0); }
.slide-in-left { opacity: 0; transform: translateX(-50px); transition: opacity 1.2s ease-out, transform 1.2s ease-out; }
.slide-in-left.appear { opacity: 1; transform: translateX(0); }

/* Misc */
.highlights { display: inline-block; border: 2px solid #fff; padding: 2px 6px; border-radius: 5px; margin-right: 5px; }

/* Responsive */
@media (max-width: 1150px) { .info-image { max-width: 60%; height: auto; } }
@media (max-width: 991px) { .info-image { max-width: 80%; height: auto; } }
@media (max-width: 768px) {
  .hero-content h1 { font-size: 2rem; }
  .hero-content p { font-size: 1rem; }
  .info-section .container { flex-direction: column; }
  .info-image { max-width: 100%; height: auto; border-radius: 12px; box-shadow: 0 8px 20px rgba(255, 255, 255, 0.05); }
}
</style>
